import { create } from "zustand";
import { DRAW_COLORS } from "@/styles/tokens";
import type { DrawingToolId } from "@/lib/lytick/drawingToolDefinitions";
import { DRAWING_TOOL_IDS, TOOL_LABELS } from "@/lib/lytick/drawingToolDefinitions";
import { safeSetItem } from "@/lib/lytick/safe-storage";

export type { DrawingToolId } from "@/lib/lytick/drawingToolDefinitions";

export type DrawingLineStyle = "solid" | "dashed";

export interface DrawingToolDefaults {
  color: string;
  lineWidth: number;
  lineStyle: DrawingLineStyle;
  opacity: number;
  text?: string;
}

/** A completed drawing that exists as a real overlay on the chart. */
export interface DrawingRecord {
  id: string;
  toolId: DrawingToolId;
  label: string;
  color: string;
  visible: boolean;
  locked: boolean;
  createdAt: number;
  lineWidth?: number;
  lineStyle?: DrawingLineStyle;
  opacity?: number;
}

interface DrawingState {
  drawings: DrawingRecord[];
  activeTool: DrawingToolId | null;
  selectedDrawingId: string | null;
  selectedDrawingIds: string[];
  drawingColor: string;
  toolDefaults: Record<DrawingToolId, DrawingToolDefaults>;
  /** Snap mode: snap drawing points to nearest OHLC values. */
  snapMode: boolean;
  /** Recent custom colors (last 5 used). */
  recentColors: string[];
  /** Drawing templates per tool type. */
  drawingTemplates: Partial<Record<DrawingToolId, DrawingToolDefaults>>;
  editingTextDrawingId: string | null;

  setActiveTool: (tool: DrawingToolId | null) => void;
  setSelectedDrawingId: (id: string | null) => void;
  setSelectedDrawingIds: (ids: string[]) => void;
  toggleDrawingSelection: (id: string) => void;
  clearDrawingSelection: () => void;
  selectAllDrawings: () => void;
  addDrawing: (drawing: DrawingRecord) => void;
  removeDrawing: (id: string) => void;
  updateDrawing: (id: string, updates: Partial<DrawingRecord>) => void;
  setDrawingColor: (color: string) => void;
  setToolDefault: (
    toolId: DrawingToolId,
    defaults: Partial<DrawingToolDefaults>,
  ) => void;
  clearDrawings: () => void;
  setSnapMode: (enabled: boolean) => void;
  toggleSnapMode: () => void;
  addRecentColor: (color: string) => void;
  saveTemplate: (toolId: DrawingToolId, config: DrawingToolDefaults) => void;
  loadTemplate: (toolId: DrawingToolId) => DrawingToolDefaults | null;
  resetAllToolDefaults: () => void;
  setEditingTextDrawingId: (id: string | null) => void;
}

const STORAGE_KEY = "lytick-drawing-defaults";
const TEMPLATES_KEY = "lytick_drawing_templates";
const RECENT_COLORS_KEY = "lytick-recent-colors";

const TOOL_IDS_LIST = [...DRAWING_TOOL_IDS] as DrawingToolId[];

function createDefaultToolDefaults(): Record<DrawingToolId, DrawingToolDefaults> {
  return TOOL_IDS_LIST.reduce(
    (accumulator, toolId) => ({
      ...accumulator,
      [toolId]: {
        color: DRAW_COLORS.bullish,
        lineWidth: 2,
        lineStyle: "solid",
        opacity: 100,
        text: (toolId === "textAnnotation" || toolId === "textTag") ? "Note" : undefined,
      },
    }),
    {} as Record<DrawingToolId, DrawingToolDefaults>,
  );
}

function loadToolDefaults() {
  const defaults = createDefaultToolDefaults();
  if (typeof window === "undefined") {
    return defaults;
  }

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return defaults;
    }

    const parsed = JSON.parse(raw) as Partial<Record<DrawingToolId, DrawingToolDefaults>>;
    return TOOL_IDS_LIST.reduce((accumulator, toolId) => {
      const next = parsed?.[toolId];
      accumulator[toolId] = {
        ...defaults[toolId],
        ...(next ?? {}),
      };
      return accumulator;
    }, { ...defaults });
  } catch {
    return defaults;
  }
}

function persistToolDefaults(toolDefaults: Record<DrawingToolId, DrawingToolDefaults>) {
  if (typeof window === "undefined") return;
  safeSetItem(STORAGE_KEY, JSON.stringify(toolDefaults), "tool defaults");
}

function loadTemplates(): Partial<Record<DrawingToolId, DrawingToolDefaults>> {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem(TEMPLATES_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as Partial<Record<DrawingToolId, DrawingToolDefaults>>;
  } catch {
    return {};
  }
}

function persistTemplates(templates: Partial<Record<DrawingToolId, DrawingToolDefaults>>) {
  if (typeof window === "undefined") return;
  safeSetItem(TEMPLATES_KEY, JSON.stringify(templates), "templates");
}

function loadRecentColors(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(RECENT_COLORS_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as string[];
  } catch {
    return [];
  }
}

function persistRecentColors(colors: string[]) {
  if (typeof window === "undefined") return;
  safeSetItem(RECENT_COLORS_KEY, JSON.stringify(colors.slice(0, 5)), "recent colors");
}

export const useDrawingStore = create<DrawingState>()((set, get) => {
  const initialDefaults = loadToolDefaults();

  return {
    drawings: [],
    activeTool: null,
    selectedDrawingId: null,
    selectedDrawingIds: [],
    drawingColor: initialDefaults.trendLine.color,
    toolDefaults: initialDefaults,
    snapMode: false,
    recentColors: loadRecentColors(),
    drawingTemplates: loadTemplates(),
    editingTextDrawingId: null,

    setActiveTool: (tool) =>
      set({
        activeTool: tool,
        selectedDrawingId: null,
        selectedDrawingIds: [],
      }),

    setSelectedDrawingId: (id) =>
      set({
        selectedDrawingId: id,
        selectedDrawingIds: id ? [id] : [],
      }),

    setSelectedDrawingIds: (ids) =>
      set(() => {
        const uniqueIds = Array.from(new Set(ids.filter(Boolean)));
        return {
          selectedDrawingId: uniqueIds.length === 1 ? uniqueIds[0] : null,
          selectedDrawingIds: uniqueIds,
        };
      }),

    toggleDrawingSelection: (id) =>
      set((state) => {
        const selected = state.selectedDrawingIds.includes(id)
          ? state.selectedDrawingIds.filter((candidate) => candidate !== id)
          : [...state.selectedDrawingIds, id];

        return {
          selectedDrawingId: selected.length === 1 ? selected[0] : null,
          selectedDrawingIds: selected,
        };
      }),

    clearDrawingSelection: () => set({ selectedDrawingId: null, selectedDrawingIds: [] }),

    selectAllDrawings: () =>
      set((state) => ({
        selectedDrawingId: null,
        selectedDrawingIds: state.drawings.map((drawing) => drawing.id),
      })),

    addDrawing: (drawing) =>
      set((state) => ({
        drawings: [...state.drawings, drawing],
      })),

    removeDrawing: (id) =>
      set((state) => {
        const selectedDrawingIds = state.selectedDrawingIds.filter(
          (drawingId) => drawingId !== id,
        );

        return {
          drawings: state.drawings.filter((drawing) => drawing.id !== id),
          selectedDrawingId:
            selectedDrawingIds.length === 1 ? selectedDrawingIds[0] : null,
          selectedDrawingIds,
        };
      }),

    updateDrawing: (id, updates) =>
      set((state) => ({
        drawings: state.drawings.map((drawing) =>
          drawing.id === id ? { ...drawing, ...updates } : drawing,
        ),
      })),

    setDrawingColor: (color) => set({ drawingColor: color }),

    setToolDefault: (toolId, defaults) =>
      set((state) => {
        const nextDefaults = {
          ...state.toolDefaults,
          [toolId]: {
            ...state.toolDefaults[toolId],
            ...defaults,
          },
        };
        persistToolDefaults(nextDefaults);

        return {
          toolDefaults: nextDefaults,
          drawingColor:
            state.activeTool === toolId
              ? nextDefaults[toolId].color
              : state.drawingColor,
        };
      }),

    clearDrawings: () => set({ drawings: [], selectedDrawingId: null, selectedDrawingIds: [] }),

    setSnapMode: (enabled) => set({ snapMode: enabled }),

    toggleSnapMode: () => set((state) => ({ snapMode: !state.snapMode })),

    addRecentColor: (color) =>
      set((state) => {
        const filtered = state.recentColors.filter((c) => c.toLowerCase() !== color.toLowerCase());
        const next = [color, ...filtered].slice(0, 5);
        persistRecentColors(next);
        return { recentColors: next };
      }),

    saveTemplate: (toolId, config) =>
      set((state) => {
        const next = { ...state.drawingTemplates, [toolId]: config };
        persistTemplates(next);
        return { drawingTemplates: next };
      }),

    loadTemplate: (toolId) => {
      return get().drawingTemplates[toolId] ?? null;
    },

    resetAllToolDefaults: () => {
      const defaults = createDefaultToolDefaults();
      persistToolDefaults(defaults);
      const activeTool = get().activeTool;
      set({
        toolDefaults: defaults,
        drawingColor: activeTool ? defaults[activeTool].color : get().drawingColor,
      });
    },

    setEditingTextDrawingId: (id) => set({ editingTextDrawingId: id }),
  };
});
